import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { AppNamesEnum } from "@/enums/appSettings";
import {
  CreateQrInputs,
  PreviewThemeResponse,
  QrCategoryProps,
  QrCodeContent,
  QrCodeContentDisplay,
  UpdateQrActivityIdInputs,
  UpdateQrInputs,
  ValidActivityDisplay,
} from "@/types/content";
import { ApiResponse, AzureUploadUrl, PaginatedResponse } from "@/types/global";

export default class QrCodeApi extends ApiVerified {
  async getQrCategoryProps(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<{ [categoryId: number]: QrCategoryProps }>(
        `qr-code/get-qr-category-props`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createQrCode(qrCreateInputs: CreateQrInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<QrCodeContent>(
        `qr-code/create-qr-code`,
        qrCreateInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrTargetUploadUrl(qrCode: string, contentType: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `qr-code/get-qr-target-upload-url`,
        { params: { qrCode: qrCode, contentType: contentType } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPreviewTheme(
    qrCategoryId: number,
    appName: AppNamesEnum,
    qrTitle: string | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PreviewThemeResponse>(`qr-code/preview-theme`, {
        params: { qrCategoryId: qrCategoryId, appName: appName, qrTitle: qrTitle },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveQrCodeContent(qrInputs: QrCodeContent): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `qr-code/save-qr-code-content`,
        qrInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateQrCodeContent(qrInputs: UpdateQrInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `qr-code/update-qr-code-content`,
        qrInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodeDetails(qrCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QrCodeContentDisplay>(
        `qr-code/get-qr-code-details`,
        { params: { qrCode: qrCode } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodeDetailsByShortCode(qrShortCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<QrCodeContentDisplay>(
        `qr-code/get-qr-code-details-by-short-code`,
        { params: { qrShortCode: qrShortCode } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllQrCodeDetails(qrCodes: Array<string>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<Array<QrCodeContentDisplay>>(
        `qr-code/get-all-qr-code-details`,
        { QrCodes: qrCodes },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodesForCategory(
    qrCategoryId: number,
    pageNumber: number,
    pageSize: number,
    versionNo: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<QrCodeContentDisplay>>(
        `qr-code/get-qr-codes-for-category`,
        {
          params: {
            qrCategoryId: qrCategoryId,
            versionNo: versionNo,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodesForChapter(
    subjectChapterId: number,
    pageNumber: number,
    pageSize: number,
    versionNo: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<QrCodeContentDisplay>>(
        // TODO(OQ-17): verify v7 endpoint — confirm qrCode chapter payload targets the Content-v2 contract
        `qr-code/get-qr-codes-for-chapter`,
        {
          params: {
            subjectChapterId: subjectChapterId,
            pageNumber: pageNumber,
            pageSize: pageSize,
            versionNo: versionNo,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getQrCodesForContent(
    qrCategoryId: number,
    contentIdsCsv: string,
    pageNumber: number,
    pageSize: number,
    versionNo: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<QrCodeContentDisplay>>(
        `qr-code/get-qr-codes-for-content`,
        {
          params: {
            qrCategoryId: qrCategoryId,
            versionNo: versionNo,
            contentIdsCsv: contentIdsCsv,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getValidActivities(qrCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ValidActivityDisplay>>(
        `qr-code/get-valid-activities`,
        { params: { qrCode: qrCode } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateQrActivityId(inputs: UpdateQrActivityIdInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `qr-code/update-qr-activity-id`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteQrCodeContent(qrCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(`qr-code/delete-qr-code-content`, {
        params: { qrCode: qrCode },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // TODO: AI code review: After implementation change this to post a service bus request
  // Actual QR generation will happen in the backend
  async autoGenerateChapterQrCodes(
    subjectChapterId: number,
    versionNo: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        // TODO(OQ-17): verify v7 endpoint — confirm qrCode chapter payload targets the Content-v2 contract
        `qr-code/auto-generate-chapter-qr-codes`,
        { SubjectChapterId: subjectChapterId, VersionNo: versionNo },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
